import { PanelSwitch } from '@/components/PanelSwitch'
import { SegmentedIndicator } from '@/components/SegmentedIndicator'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { PerformanceMetric } from '@/lib/performance.types'
import { METRIC_LABEL } from '@/lib/performanceFormat'
import { useTabDirection } from '@/lib/useTabDirection'
import type { PerformanceChartsProps } from './PerformanceCharts.types'
import { PerformanceMetricChart } from './PerformanceMetricChart'

const METRICS: readonly PerformanceMetric[] = [
  'visibilityRate',
  'weightedScorePercent',
  'shareOfVoice',
]

const TAB_INDICATOR = 'performance-metric-tab-indicator'

function isPerformanceMetric(value: string): value is PerformanceMetric {
  return (
    value === 'visibilityRate' ||
    value === 'weightedScorePercent' ||
    value === 'shareOfVoice'
  )
}

// Expanded stacks all three fixed-scale metrics vertically; Focused shows one behind metric tabs.
export function PerformanceCharts({
  points,
  viewMode,
}: PerformanceChartsProps) {
  const tabs = useTabDirection(METRICS, 'visibilityRate')

  if (viewMode === 'expanded') {
    return (
      <div className="space-y-6">
        {METRICS.map((metric) => (
          <Card key={metric}>
            <CardHeader>
              <h3 className="text-sm font-medium text-muted-foreground">
                {METRIC_LABEL[metric]}
              </h3>
            </CardHeader>
            <CardContent>
              <PerformanceMetricChart
                points={points}
                metric={metric}
                size="expanded"
              />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <Card className="gap-0">
      <CardHeader className="pb-0">
        <Tabs
          value={tabs.active}
          onValueChange={(value) => {
            if (!isPerformanceMetric(value)) return
            tabs.select(value)
          }}
        >
          <TabsList variant="line">
            {METRICS.map((metric) => (
              <TabsTrigger key={metric} value={metric} className="relative">
                {tabs.active === metric && (
                  <SegmentedIndicator
                    layoutId={TAB_INDICATOR}
                    className="absolute inset-x-0 bottom-0 h-0.5 bg-primary"
                  />
                )}
                {METRIC_LABEL[metric]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </CardHeader>

      <CardContent className="pt-4">
        <PanelSwitch
          panelKey={tabs.active}
          index={tabs.index}
          previousIndex={tabs.previousIndex}
        >
          <PerformanceMetricChart
            points={points}
            metric={tabs.active}
            size="focused"
          />
        </PanelSwitch>
      </CardContent>
    </Card>
  )
}
